import { Dispatch, SetStateAction } from 'react';
import { useQueryParams } from './useQueryParams';
import { modifyURLState } from '../utils/utils-routing';
import { useHistory } from 'react-router';

type PaginationAndSortParams = {
    readonly page: number;
    readonly setPage: Dispatch<SetStateAction<number>>;
    readonly pageSize: number;
    readonly setPageSize: Dispatch<SetStateAction<number>>;
    readonly sortBy: string;
    readonly setSortBy: (newSortOrder: string) => void;
};

type PaginationAndSortOpts = {
    readonly defaultPage?: number;
    readonly defaultPageSize?: number;
    readonly defaultSortBy?: string;
    readonly shouldResetPageOnSortChange?: boolean;
};

export function usePaginationAndSortParams({
    defaultPage = 0,
    defaultPageSize = 50,
    defaultSortBy = 'orderDateNewToOld',
    shouldResetPageOnSortChange = false
}: PaginationAndSortOpts = {}): PaginationAndSortParams {
    const history = useHistory();
    const {
        page: { value: pageParam = defaultPage },
        pageSize: { value: pageSizeParam = defaultPageSize },
        sortBy: { value: sortByParam = defaultSortBy }
    } = useQueryParams({
        page: 'integer',
        pageSize: 'integer',
        sortBy: 'string'
    });

    const page = Math.max(0, pageParam - 1);
    const setPage = nextPage => {
        const nextPageParam = nextPage !== 0 ? nextPage + 1 : undefined;
        const nextSearch = modifyURLState(history.location.search, 'page', nextPageParam, false);

        history.replace({ pathname: history.location.pathname, search: nextSearch });
    };

    const sortBy = sortByParam;
    const setSortBy = (newSortOrder: string) => {
        if (shouldResetPageOnSortChange) setPage(0);

        const nextSearch = modifyURLState(history.location.search, 'sortBy', newSortOrder, false);
        history.replace({ pathname: history.location.pathname, search: nextSearch });
    };

    const pageSize = Math.max(0, pageSizeParam);
    const setPageSize = nextPageSize => {
        const nextSearch = modifyURLState(history.location.search, 'pageSize', nextPageSize, false);
        history.replace({ pathname: history.location.pathname, search: nextSearch });
    };

    return {
        page,
        setPage,
        pageSize,
        setPageSize,
        sortBy,
        setSortBy
    };
}
